import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import "@/lib/i18n";  // bootstrap i18next (English default)
import LoginPage from "@/pages/LoginPage";
import AppShell from "@/components/layout/AppShell";
import DashboardPage from "@/pages/DashboardPage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerNewPage from "@/pages/CustomerNewPage";
import ApplicationsPage from "@/pages/ApplicationsPage";
import ApplicationNewPage from "@/pages/ApplicationNewPage";
import ApplicationDetailPage from "@/pages/ApplicationDetailPage";
import DocumentsPage from "@/pages/DocumentsPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import AdminPage from "@/pages/AdminPage";
import ReportsPage from "@/pages/ReportsPage";
import MasterDataPage from "@/pages/MasterDataPage";
import PolicyRulesPage from "@/pages/PolicyRulesPage";
import MakerCheckerPage from "@/pages/MakerCheckerPage";
import MisPage from "@/pages/MisPage";
import SettingsPage from "@/pages/SettingsPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import BankFormPage from "@/pages/BankFormPage";
import NotificationsPage from "@/pages/NotificationsPage";
import CustomersBulkUploadPage from "@/pages/CustomersBulkUploadPage";
import FOQueuePage from "@/pages/FOQueuePage";
import DraftsPage from "@/pages/DraftsPage";
import ReportsBuilderPage from "@/pages/ReportsBuilderPage";
import LoanProductsPage from "@/pages/LoanProductsPage";
import LeadsPage from "@/pages/LeadsPage";
import LegacyUploadPage from "@/pages/LegacyUploadPage";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="customers/new" element={<CustomerNewPage />} />
              <Route path="customers/bulk-upload" element={<CustomersBulkUploadPage />} />
              <Route path="drafts" element={<DraftsPage />} />
              <Route path="fo-queue" element={<FOQueuePage />} />
              <Route path="applications" element={<ApplicationsPage />} />
              <Route path="applications/new" element={<ApplicationNewPage />} />
              <Route path="applications/:id" element={<ApplicationDetailPage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="approvals" element={<ApprovalsPage />} />
              <Route path="maker-checker" element={<MakerCheckerPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="reports/builder" element={<ReportsBuilderPage />} />
              <Route path="leads" element={<LeadsPage />} />
              <Route path="admin/products" element={<LoanProductsPage />} />
              <Route path="customers/legacy-upload" element={<LegacyUploadPage />} />
              <Route path="mis" element={<MisPage />} />
              <Route path="admin" element={<AdminPage />} />
              <Route path="admin/master" element={<MasterDataPage />} />
              <Route path="admin/policy" element={<PolicyRulesPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="super-admin" element={<SuperAdminPage />} />
              <Route path="super-admin/banks/new" element={<BankFormPage />} />
              <Route path="super-admin/banks/:id" element={<BankFormPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}
